import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GroupSearchCriteriaModel } from '@app/core/models/groups/group-search-criteria.model';
import { GroupTypeModel } from '@app/core/models/groups/group-type.model';
import { GroupModel } from '@app/core/models/groups/group.model';
import { InvoiceForwardModel } from '@app/core/models/invoices/invoice-forward.model';
import { ReferentialItem } from '@app/core/models/shared/referential-item.model';
import { GroupService } from '@app/core/services/group.service';
import { InvoiceService } from '@app/core/services/invoice.service';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { AlertType } from '@app/shared/models/alert-type.model';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import * as _ from 'lodash';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-invoice-forward',
  templateUrl: './invoice-forward.component.html',
  styleUrls: ['./invoice-forward.component.scss'],
})
export class InvoiceForwardComponent implements OnInit, OnDestroy {
  InvoiceNumber: string;
  InvoiceId: string;
  InvoiceViewType: string;
  showPickedWarning: boolean;
  assignmentTargetGroups: number[];
  supervisionGroupsId: number[] = [];
  groups: ReferentialItem[];
  groupDropDownSettings: IDropdownSettings;
  groupSearchEventSubject: Subject<string> = new Subject<string>();
  groupSearchEvent$: Observable<string> =
    this.groupSearchEventSubject.asObservable();
  unsubscribe$: Subject<boolean> = new Subject<boolean>();
  selectedGroupIsSupervision: boolean = false;
  originGroupIsOperational: boolean = false;
  comment: string;

  constructor(
    private dialogRef: MatDialogRef<InvoiceForwardComponent>,
    public dialog: MatDialog,
    private groupService: GroupService,
    private invoiceService: InvoiceService,
    @Inject(MAT_DIALOG_DATA) data
  ) {
    this.InvoiceNumber = data.InvoiceNumber;
    this.InvoiceId = data.InvoiceId;
    this.InvoiceViewType = data.InvoiceViewType;
    this.showPickedWarning = data.ShowPickedWarning;
    this.originGroupIsOperational = data.OriginGroupIsOperational;
    this.groupService
      .getGroupsByType([GroupTypeModel.SUPERVISION])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((groups: GroupModel[]) => {
        this.supervisionGroupsId = groups.map((g) => g.Id);
      });
  }

  get showComment(): boolean {
    return this.originGroupIsOperational && this.selectedGroupIsSupervision;
  }

  ngOnInit(): void {
    this.groupDropDownSettings = new MultiSelectModel().getSettings(
      'Id',
      'Label',
      true,
      true,
      true
    );
    this.groupDropDownSettings.allowRemoteDataSearch = true;

    this.groupSearchEvent$
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(250),
        distinctUntilChanged(),
        filter((filterData) => !_.isNil(filterData))
      )
      .subscribe((filterData) => {
        this.groupService
          .getPagedGroups(
            {
              GroupName: filterData,
              GroupTypes: [
                GroupTypeModel.SUPERVISION,
                GroupTypeModel.OPERATIONAL,
              ],
            } as GroupSearchCriteriaModel,
            'Label',
            'desc',
            0,
            30
          )
          .subscribe((data) => {
            this.groups = data.Items;
          });
      });

    this.groupSearchEventSubject.next('');
  }

  updateSelectedGroupIsSupervision(selectedGroupId: number): void {
    this.selectedGroupIsSupervision =
      this.supervisionGroupsId.includes(selectedGroupId);
  }

  public onGroupsFilterChange(data: string) {
    this.groupSearchEventSubject.next(data);
  }

  onSubmit(): void {
    const dialog = this.dialog.open(ModalComponent, {
      height: '12rem',
      width: '30rem',
      disableClose: true,
      data: {
        cancelActionName: 'Annuler',
        validateActionName: 'Confirmer',
        alertType: AlertType.CONFIRMATION,
        modalMessage: 'Êtes-vous sûr de vouloir transférer cette facture ?',
      },
    });

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((confirm: boolean) => {
        if (confirm) {
          this.onConfirm();
        }
      });
  }

  onSelectGroup(selectedItem: ReferentialItem) {
    this.updateSelectedGroupIsSupervision(selectedItem.Id);
  }

  onDeSelectGroup() {
    this.selectedGroupIsSupervision = false;
  }

  onConfirm() {
    const forwardRequest = this.getModel();
    this.invoiceService
      .forwardInvoice(this.InvoiceId, forwardRequest)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        () => {
          this.close(true);
        },
        (_) => {
          this.close(false);
        }
      );
  }

  getModel(): InvoiceForwardModel {
    const model = new InvoiceForwardModel();
    model.TargetGroupId = _.head(this.assignmentTargetGroups)['Id'];
    if (this.showComment) {
      model.Comment = this.comment;
    }

    return model;
  }

  close(isSuccess: boolean) {
    this.dialogRef.close(isSuccess);
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }

  setComment(event: Event){
    const value = (event.target as any).value;
    this.comment = value;
  }
}
